/**
 * Code Executor V2
 * Secure, cross-platform JavaScript code executor using AST interpretation
 */

import { parse } from 'acorn'
import { validate } from './validator.js'
import { createInterpreter } from './interpreter.js'
import { createGlobals } from './safe-globals.js'
import { ParseError, TimeoutError, SandboxError } from './errors.js'

/**
 * Create a code executor instance
 * @param {Object} options - Executor options
 * @param {number} [options.timeout=30000] - Execution timeout in ms
 * @param {number} [options.maxIterations=10000] - Maximum loop iterations
 * @param {Object} options.tools - Tools to inject into sandbox
 * @returns {Object} Executor instance
 */
export function createExecutor(options = {}) {
  const { timeout = 30000, maxIterations = 10000, tools = {} } = options

  const globals = createGlobals(tools)
  const interpreter = createInterpreter(globals)

  return {
    /**
     * Execute code string
     * @param {string} code - JavaScript code to execute
     * @param {Object} [execOptions] - Override options for this execution
     * @returns {Promise<Object>} Execution result
     */
    async execute(code, execOptions = {}) {
      const actualTimeout = execOptions.timeout || timeout
      const actualMaxIterations = execOptions.maxIterations || maxIterations

      try {
        // Parse
        const ast = parseCode(code)

        // Validate
        validate(ast)

        // Execute with timeout
        const result = await executeWithTimeout(
          () => interpreter.execute(ast, { maxIterations: actualMaxIterations }),
          actualTimeout
        )

        return {
          success: true,
          result,
          output: formatOutput(result),
        }
      } catch (error) {
        return {
          success: false,
          error: error.message,
          errorType: error instanceof SandboxError ? error.type : 'runtime',
          location: error.location || null,
          output: `Error: ${error.message}`,
        }
      }
    },
  }
}

/**
 * Parse code string to AST
 * Wraps code in async function to allow top-level return and await
 */
function parseCode(code) {
  // Wrap in async function to allow top-level return and await
  const wrappedCode = `(async () => { ${code} })()`

  try {
    return parse(wrappedCode, {
      ecmaVersion: 'latest',
      sourceType: 'script',
      locations: true,
    })
  } catch (error) {
    // Adjust error location to account for wrapper
    const line = error.loc?.line || 1
    const column = error.loc?.column || 0
    // The wrapper adds "(async () => { " which is 16 chars
    const adjustedColumn = line === 1 ? Math.max(0, column - 16) : column
    throw new ParseError(error.message, {
      line,
      column: adjustedColumn,
    })
  }
}

/**
 * Execute with timeout
 */
async function executeWithTimeout(fn, timeout) {
  return Promise.race([
    fn(),
    new Promise((_, reject) =>
      setTimeout(() => reject(new TimeoutError(timeout)), timeout)
    ),
  ])
}

/**
 * Format output for display
 */
function formatOutput(result) {
  if (result === undefined) return 'undefined'
  if (result === null) return 'null'
  if (typeof result === 'object') {
    try {
      return JSON.stringify(result, null, 2)
    } catch {
      return String(result)
    }
  }
  return String(result)
}

/**
 * Simple execute function for quick usage
 * @param {string} code - JavaScript code
 * @param {Object} tools - Tools to inject
 * @param {Object} [options] - Execution options
 * @returns {Promise<Object>} Execution result
 */
export async function execute(code, tools = {}, options = {}) {
  const executor = createExecutor({ ...options, tools })
  return executor.execute(code)
}

// Export errors for external use
export { SandboxError, ParseError, TimeoutError } from './errors.js'
export { ValidationError, RuntimeError, IterationLimitError } from './errors.js'
